import { IntradayStrategyStartRequest, TradingWsEnvelope } from '../types/trading';

export type TradingSocketEventType =
  | 'tick_l1'
  | 'order_event'
  | 'portfolio_state_update'
  | 'risk_reject'
  | 'system_alert'
  | 'intraday_signal_update'
  | 'blotter_status_update'
  | 'subscription_result';

const VALID_EVENT_TYPES: TradingSocketEventType[] = [
  'tick_l1',
  'order_event',
  'portfolio_state_update',
  'risk_reject',
  'system_alert',
  'intraday_signal_update',
  'blotter_status_update',
  'subscription_result',
];

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const isTradingEventType = (value: unknown): value is TradingSocketEventType =>
  typeof value === 'string' && VALID_EVENT_TYPES.includes(value as TradingSocketEventType);

export const parseTradingWsEnvelope = (raw: unknown): TradingWsEnvelope | null => {
  let decoded: unknown = raw;
  if (typeof raw === 'string') {
    try {
      decoded = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (!isObject(decoded)) return null;
  if (!isTradingEventType(decoded.type)) return null;
  if (!Object.prototype.hasOwnProperty.call(decoded, 'data')) return null;

  return {
    type: decoded.type,
    data: decoded.data,
  };
};

export type TradingSocketCommandType = 'subscribe' | 'unsubscribe';

export type IntradaySignalStartCommandPayload = IntradayStrategyStartRequest[];

export const normalizeTradingSymbol = (symbol: string): string =>
  symbol.trim().toUpperCase().replace(/^O:/, '');

export const buildTradingSocketCommand = (type: TradingSocketCommandType, symbol: string): string =>
  JSON.stringify({
    type,
    symbol: normalizeTradingSymbol(symbol),
  });

const normalizeStrategies = (payload: IntradaySignalStartCommandPayload) =>
  payload.map((strategy) => ({
    ...strategy,
    symbol: normalizeTradingSymbol(strategy.symbol),
  }));

export const buildIntradaySignalStartCommand = (
  payload: IntradaySignalStartCommandPayload,
): string =>
  JSON.stringify({
    type: 'intraday_signal_start',
    strategies: normalizeStrategies(payload),
  });

export const buildIntradaySignalTakeOverCommand = (
  payload: IntradaySignalStartCommandPayload,
): string =>
  JSON.stringify({
    type: 'intraday_signal_take_over',
    strategies: normalizeStrategies(payload),
  });

export const buildIntradaySignalStopCommand = (strategyId?: string): string =>
  JSON.stringify(
    strategyId ? { type: 'intraday_signal_stop', strategy_id: strategyId } : { type: 'intraday_signal_stop' },
  );
